import type { FunctionComponent } from 'react';

import { useNavigate } from 'react-router';

import { Badge, Button, Center, Group, Loader, Menu, Text, Tooltip } from '@mantine/core';
import { useDisclosure } from '@mantine/hooks';

import { useTranslation } from 'i18n';

import { PlusCircleIconSm, ChevronDownIconXs } from '@chownow/pantry/icons';
import { getCaretStyle, MENU_DROPDOWN_WIDTH } from './helpers';

type CreateMenuDropdownProps = {
  isSingleMerchantType: boolean;
  isMobile: boolean;
  publishLocked?: boolean;
  isBuildActive?: boolean;
  isLoadingIntegrations: boolean;
  merchantId: string;
  onMenuOpen: () => void;
  onOpenImportDrawer: () => void;
  setCreateMenu: (merchantId: string) => void;
};

export const CreateMenuDropdown: FunctionComponent<CreateMenuDropdownProps> = ({
  isSingleMerchantType,
  isMobile,
  publishLocked,
  isBuildActive = false,
  isLoadingIntegrations,
  merchantId,
  onMenuOpen,
  onOpenImportDrawer,
  setCreateMenu,
}) => {
  // Translations live in containers/MenuUpload/__translations__ (menuUpload namespace)
  const { t } = useTranslation('menuUpload');
  const navigate = useNavigate();
  const [menuOpen, menuHandlers] = useDisclosure();
  const size = isSingleMerchantType && !isMobile ? 'md' : 'sm';

  return (
    <Menu
      position="bottom-end"
      onOpen={() => {
        menuHandlers.open();
        onMenuOpen();
      }}
      onClose={menuHandlers.close}
    >
      <Menu.Target>
        <Button
          variant={isSingleMerchantType ? 'filled' : 'ghost'}
          size={size}
          leftSection={isSingleMerchantType ? undefined : <PlusCircleIconSm />}
          rightSection={
            <span style={getCaretStyle(menuOpen)}>
              <ChevronDownIconXs />
            </span>
          }
        >
          {t('CreateMenuDropdown.create')}
        </Button>
      </Menu.Target>
      <Menu.Dropdown w={MENU_DROPDOWN_WIDTH}>
        {isLoadingIntegrations ? (
          <Center p="md">
            <Loader size="sm" />
          </Center>
        ) : (
          <>
            <Menu.Item
              disabled={publishLocked}
              onClick={() => {
                setCreateMenu(merchantId);
                void navigate(`create?merchantId=${merchantId}`);
              }}
            >
              <Text variant="label-md">{t('CreateMenuDropdown.useBlankMenu')}</Text>
            </Menu.Item>
            <Menu.Item onClick={onOpenImportDrawer}>
              <Text variant="label-md">{t('CreateMenuDropdown.importFromThirdParty')}</Text>
            </Menu.Item>
            <Tooltip
              label={t('CreateMenuDropdown.buildInProgressTooltip')}
              disabled={!isBuildActive}
              position="left"
            >
              <Menu.Item
                disabled={isBuildActive}
                onClick={() => void navigate('upload', { state: { merchantId } })}
              >
                <Group justify="space-between" wrap="nowrap">
                  <Text variant="label-md">{t('CreateMenuDropdown.generateMenu')}</Text>
                  <Badge size="xs" variant="blue">
                    {t('CreateMenuDropdown.new')}
                  </Badge>
                </Group>
              </Menu.Item>
            </Tooltip>
          </>
        )}
      </Menu.Dropdown>
    </Menu>
  );
};
